'use client';

import { c as _c } from "react/compiler-runtime";
import { OutcomeIdentityImage } from '@/components/market/identity/outcome-identity-image';
import type { OutcomeHueName } from '@/config/outcome-colors';
import { cn } from '@/lib/ui/utils';

/**
 * Tiny filled circle in the outcome's color — used by minimal pickers
 * (trade form, market traders / chart filters) where the full ringed
 * `OutcomeIndicator` would be too busy. The color is a runtime-derived
 * CSS variable (via `outcomeHueToStepVar`), so it must be applied via
 * the `style` prop. Co-located here so the FQ-002 inline-style exemption
 * stays scoped to this file.
 */
export function OutcomeDot(t0) {
  const $ = _c(7);
  const {
    color,
    dim: t1
  } = t0;
  const dim = t1 === undefined ? false : t1;
  const t2 = dim && "opacity-40";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("size-2 shrink-0 rounded-full", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== color) {
    t4 = {
      backgroundColor: color
    };
    $[2] = color;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <span aria-hidden={true} className={t3} style={t4} />;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IndicatorSize = 'xs' | 'sm' | 'md' | 'lg';
interface OutcomeIndicatorProps {
  /**
   * Outcome color (hex / hsl / CSS var). Used only as the coloured ring
   * around the dithered image — identity reinforcement that matches the
   * bar / chart colour. Hue + image content come from the lib via the
   * façade, not from this prop.
   */
  color: string;
  /** Optional image URL for the outcome. When `null` / empty, the lib renders a synthetic placeholder. */
  imageUrl?: string | undefined;
  /**
   * NSFW flag for `imageUrl` (spec 071). When true the rendered image is
   * blurred and the NSFW reveal overlay is layered above until the viewer
   * taps to reveal. Optional — defaults to `false` at the callsite when
   * the parent has a backend version that predates the field.
   */
  imageNsfw?: boolean;
  /** Parent market's pubkey — used as the deterministic seed source for the lib. */
  marketPubkey: string;
  /** Outcome index — combines with `marketPubkey` to seed the lib. */
  outcomeIndex: number;
  /**
   * Outcome hue — must come from `useOutcomeColor(...).hue` so the
   * dithered image palette and chart series colour stay in lockstep.
   */
  hue: OutcomeHueName | undefined;
  /** Display size. xs=20px / sm=24px / md=28px / lg=32px. Default: sm. */
  size?: IndicatorSize;
  /** Add a ring separator (for overlapped grouped indicators). */
  ring?: boolean;
  /** Additional classes on the outer element. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Tailwind size classes for the indicator container — image variant. */
const SIZE_CLASSES: Record<IndicatorSize, string> = {
  xs: 'size-5',
  sm: 'size-6',
  md: 'size-7',
  lg: 'size-8'
};

/** Tailwind size classes for the dot variant (no uploaded image). Smaller
 *  than the image container — a solid coloured dot is a marker, not a tile. */
const DOT_SIZE_CLASSES: Record<IndicatorSize, string> = {
  xs: 'size-2',
  sm: 'size-2.5',
  md: 'size-3',
  lg: 'size-4'
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Unified outcome indicator — always renders a circular dithered image
 * via `<OutcomeIdentityImage>` with `entity={{ kind: 'outcome', ... }}`. When
 * `imageUrl` is null / empty the lib synthesises a placeholder pattern
 * deterministically seeded from `(marketPubkey, outcomeIndex)`. The
 * coloured ring around the image stays — it's identity reinforcement
 * that matches the outcome's bar / chart colour.
 *
 * Use this everywhere an outcome marker appears: legend, trade form,
 * holdings, market info, chart tooltip, etc.
 *
 * Note: at xs/sm sizes (20–24 px) the dithered tile is essentially noise.
 * Acceptable for v1 per the images-plan UX notes — no special-casing.
 */
export function OutcomeIndicator(t0) {
  const $ = _c(25);
  const {
    color,
    imageUrl,
    imageNsfw: t1,
    marketPubkey,
    outcomeIndex,
    hue,
    size: t2,
    ring: t3,
    className
  } = t0;
  const imageNsfw = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "sm" : t2;
  const ring = t3 === undefined ? false : t3;
  const normalisedImageUrl = imageUrl !== undefined && imageUrl !== "" ? imageUrl : null;
  if (normalisedImageUrl === null) {
    const t4 = DOT_SIZE_CLASSES[size];
    const t5 = ring && "ring-1 ring-background-secondary";
    let t6;
    if ($[0] !== className || $[1] !== t4 || $[2] !== t5) {
      t6 = cn("inline-block shrink-0 rounded-full", t4, t5, className);
      $[0] = className;
      $[1] = t4;
      $[2] = t5;
      $[3] = t6;
    } else {
      t6 = $[3];
    }
    let t7;
    if ($[4] !== color) {
      t7 = {
        backgroundColor: color
      };
      $[4] = color;
      $[5] = t7;
    } else {
      t7 = $[5];
    }
    let t8;
    if ($[6] !== t6 || $[7] !== t7) {
      t8 = <span className={t6} style={t7} aria-hidden="true" />;
      $[6] = t6;
      $[7] = t7;
      $[8] = t8;
    } else {
      t8 = $[8];
    }
    return t8;
  }
  const borderPx = size === "xs" ? "1.5px" : size === "sm" ? "2px" : "2.5px";
  const t4 = SIZE_CLASSES[size];
  const t5 = ring && "ring-1 ring-background-secondary";
  let t6;
  if ($[9] !== className || $[10] !== t4 || $[11] !== t5) {
    t6 = cn("inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full", t4, t5, className);
    $[9] = className;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `${borderPx} solid ${color}`;
  let t8;
  if ($[13] !== t7) {
    t8 = {
      border: t7
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== hue || $[16] !== imageNsfw || $[17] !== marketPubkey || $[18] !== normalisedImageUrl || $[19] !== outcomeIndex) {
    t9 = <OutcomeIdentityImage variant="thumbnail" entity={{
      kind: "outcome",
      marketPubkey,
      outcomeIndex,
      hue,
      imageUrl: normalisedImageUrl,
      nsfw: imageNsfw
    }} alt="" size="size-full" rounded="rounded-full" preview={false} />;
    $[15] = hue;
    $[16] = imageNsfw;
    $[17] = marketPubkey;
    $[18] = normalisedImageUrl;
    $[19] = outcomeIndex;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t8 || $[23] !== t9) {
    t10 = <span className={t6} style={t8}>{t9}</span>;
    $[21] = t6;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}